import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdaterChainId } from '~/hooks/web3'
import { useSpotSwapPair, useSpotSwapTokens } from '~/state/dex/hooks'
import { AddressZero } from '@ethersproject/constants'
import { Currency, FixedPointNumber } from '~/utils/fixedPoint'
import { formatFormAmount } from '~/utils/numbers'
import { useTokenPriceWithUseAPI } from '~/hooks/useTokenPrice'
import BigNumber from 'bignumber.js'
import { Container, StyledLabel, StyledDiscountedBtn } from './styled'
import { StyledDetailValue } from '../OrderPreview/styled'
import { formatUnits } from 'viem'
import Skeleton from '~/components/Exchange/components/Skeleton'
import Tooltip from '~/components/Tooltip'
import {
  useIsSendByRelayer,
  useCalcGasFeeRisk,
} from '~/components/Exchange/hooks'

const formatGasFeeValueLiteral = (
  gasFeeValue: BigNumber | undefined,
  isGasFeeZero: boolean,
): string => {
  if (isGasFeeZero || !Number(gasFeeValue)) {
    return '~'
  }
  if (Number(gasFeeValue) < 0.01) {
    return '< $0.01'
  }
  const formatted = new FixedPointNumber(gasFeeValue).toCurrency(
    Currency.USD,
    2,
  )
  return `~${formatted}`
}

const GasFeeRow: React.FC<{
  loading: boolean
  gasFeeTokenAmount: bigint
}> = ({ loading, gasFeeTokenAmount }) => {
  const { t } = useTranslation()
  const expectChainId = useUpdaterChainId()
  const [, outputToken] = useSpotSwapPair()
  const tokens = useSpotSwapTokens(expectChainId)
  const isSendByRelayer = useIsSendByRelayer()
  const isGasFeeZero = gasFeeTokenAmount === 0n

  const nativeToken = tokens[AddressZero]
  const nativeTokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: AddressZero,
  })
  const outputPrice = useTokenPriceWithUseAPI({
    tokenAddress: outputToken?.address,
    chainId: outputToken?.chainId,
  })
  const price = isSendByRelayer ? outputPrice : nativeTokenPrice
  const symbol = isSendByRelayer ? outputToken?.symbol : nativeToken?.symbol

  const { level } = useCalcGasFeeRisk(gasFeeTokenAmount)

  const formattedGasFee = useMemo(() => {
    const decimals = isSendByRelayer
      ? outputToken?.decimals
      : nativeToken?.decimals
    return formatUnits(gasFeeTokenAmount, decimals)
  }, [
    gasFeeTokenAmount,
    isSendByRelayer,
    outputToken?.decimals,
    nativeToken?.decimals,
  ])

  const gasFeeValueLiteral = useMemo(() => {
    const gasFeeValue = new BigNumber(formattedGasFee).times(price)
    return formatGasFeeValueLiteral(gasFeeValue, isGasFeeZero)
  }, [formattedGasFee, price])

  // 代上链固定展示 Free
  const label = isSendByRelayer ? t('gas_fee') : t('estimate_gas_fee')

  const GasFeeValue = () => {
    if (isSendByRelayer) {
      return (
        <Tooltip
          placement={'right'}
          mobilePlacement="left"
          title={t('relayer_gas_fee_tip')}
          trigger={['hover', 'click']}
        >
          <StyledDiscountedBtn>{'Free'}</StyledDiscountedBtn>
        </Tooltip>
      )
    }

    return loading ? (
      <Skeleton />
    ) : (
      <Tooltip
        trigger="hover"
        placement="right"
        title={t('spotswap_estimate_gas_fee_tooltip')}
        tooltipTitle={t('estimate_gas_fee')}
        tooltipTitleValue={`${formatFormAmount(formattedGasFee)} ${symbol}`}
      >
        <StyledDetailValue level={level === 'info' ? level : 'warning'}>
          {gasFeeValueLiteral}
        </StyledDetailValue>
      </Tooltip>
    )
  }

  return (
    <Container>
      <StyledLabel>{label}</StyledLabel>
      <GasFeeValue />
    </Container>
  )
}

export default GasFeeRow
